import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
// Importamos Eye e EyeOff para a funcionalidade de senha
import { Lock, Mail, User, Shield, Eye, EyeOff } from 'lucide-react'; 
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { register as svcRegister } from '@/services/auth.service';
import { useAuth } from '@/context/AuthContext';

const Register = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: ''
  });
  const [loading, setLoading] = useState(false);
  // NOVOS ESTADOS PARA VISIBILIDADE DE SENHA
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  // Funções para alternar visibilidade
  const togglePasswordVisibility = () => setShowPassword(prev => !prev);
  const toggleConfirmPasswordVisibility = () => setShowConfirmPassword(prev => !prev);


  const handleSubmit = async (e) => {
    e.preventDefault();

    if (formData.password !== formData.confirmPassword) {
      toast.error('As senhas não coincidem');
      return;
    }

    if (formData.password.length < 6) {
      toast.error('A senha deve ter pelo menos 6 caracteres');
      return;
    }

    setLoading(true);

    try {
      const data = await svcRegister(formData.email, formData.password, formData.name);
      login(data.access_token, data.user);
      toast.success('Conta criada com sucesso!');
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar conta');
    } finally {
      setLoading(false);
    }
  };

  return (
    // ESTILO DE FUNDO DO LOGIN
    <div className="min-h-screen flex items-center justify-center gradient-security-bg p-4">
      {/* Padrão de Fundo (igual ao Login) */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0" style={{ backgroundImage: "radial-gradient(circle at 20% 80%, rgba(243, 156, 18, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(44, 62, 80, 0.3) 0%, transparent 50%)" }}></div>
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo/Header (ESTILO DO LOGIN) */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-white rounded-full mb-4 shadow-lg p-3"
            style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)' }}>
            <Shield className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-4xl font-bold text-white mb-2" style={{ fontFamily: 'Inter, sans-serif' }}>
            JURIS<span style={{ color: '#F39C12' }}>KEY</span>
          </h1>
          <p className="text-white opacity-90">Crie sua conta gratuitamente</p>
        </div>

        {/* Register Card (ESTILO DO LOGIN) */}
        <div className="relative bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full animate-fade-in">
          <h2 className="text-2xl font-bold mb-6 text-center font-sans" style={{ color: '#2C3E50' }}>
            Criar nova conta
          </h2>

          <form onSubmit={handleSubmit} className="space-y-4"> {/* Espaçamento ajustado */}
            {/* CAMPO NOME */}
            <div className="relative">
              <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="Digite seu nome completo"
                value={formData.name}
                onChange={handleChange}
                required
                data-testid="register-name-input"
                className="w-full pl-10 pr-4 py-2.5 h-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-gray-900 placeholder-gray-500 font-sans"
              />
            </div>

            {/* CAMPO EMAIL */}
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                id="email"
                name="email"
                type="email"
                placeholder="Digite seu e-mail"
                value={formData.email}
                onChange={handleChange}
                required
                data-testid="register-email-input"
                className="w-full pl-10 pr-4 py-2.5 h-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-gray-900 placeholder-gray-500 font-sans"
              />
            </div>

            {/* CAMPO SENHA COM VISIBILIDADE */}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                id="password"
                name="password"
                // TIPO DINÂMICO
                type={showPassword ? "text" : "password"}
                placeholder="Defina sua senha"
                value={formData.password}
                onChange={handleChange}
                required
                data-testid="register-password-input"
                className="w-full pl-10 pr-10 py-2.5 h-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-gray-900 placeholder-gray-500 font-sans"
              />
              {/* BOTÃO DE ALTERNÂNCIA */}
              <button
                type="button"
                onClick={togglePasswordVisibility}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* CAMPO CONFIRMAR SENHA COM VISIBILIDADE */}
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <Input
                id="confirmPassword"
                name="confirmPassword"
                // TIPO DINÂMICO
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirme sua senha"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                data-testid="register-confirm-password-input"
                className="w-full pl-10 pr-10 py-2.5 h-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-400 text-gray-900 placeholder-gray-500 font-sans"
              />
              {/* BOTÃO DE ALTERNÂNCIA */}
              <button
                type="button"
                onClick={toggleConfirmPasswordVisibility}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 p-1"
                aria-label={showConfirmPassword ? "Ocultar senha" : "Mostrar senha"}
              >
                {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>

            {/* BOTÃO PRIMÁRIO DOURADO (ESTILO DO LOGIN) */}
            <Button
              type="submit"
              disabled={loading}
              data-testid="register-submit-button"
              className="w-full"
              style={{ background: 'linear-gradient(135deg, #F39C12, #E67E22)', color: 'white', height: '2.5rem', borderRadius: '0.5rem', fontWeight: '600' }}
            >
              {loading ? 'Criando conta...' : 'Criar conta'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-gray-600">
              Já tem uma conta?{' '}
              <Link to="/login" className="font-semibold hover:underline" style={{ color: '#F39C12' }} data-testid="login-link">
                Entrar
              </Link>
            </p>
          </div>
        </div>

        {/* Footer/Security Info (ESTILO DO LOGIN) */}
        <div className="mt-6 text-center text-sm text-white opacity-80">
          <p>© 2025 JURISKEY. Todos os direitos reservados.</p>
        </div>
      </div>
    </div>
  );
};

export default Register;